import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/components/ui/use-toast';
import { getBusinessProposals, getOpportunityByAnyId } from '@/services/business-opportunities';
import { BusinessOpportunity, BusinessProposal, OpportunityStatus } from '@/types/business-opportunities';
import { formatCurrency } from '@/lib/utils';
import { AlertCircle, ArrowLeft, Building, Calendar, Edit, FileText, Loader2, User } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

// Definição de tipo simplificada para oportunidade com detalhes
type OpportunityWithDetails = BusinessOpportunity & {
  customer: {
    id: string;
    name: string;
    logo_url?: string;
    business_identification_number?: string;
  };
  contact?: {
    id: string;
    name?: string;
    email?: string;
    phone?: string;
  };
};

const OpportunityDetailSimple: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  // Estados
  const [opportunity, setOpportunity] = useState<OpportunityWithDetails | null>(null);
  const [proposals, setProposals] = useState<BusinessProposal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');
  const [errorDetails, setErrorDetails] = useState<{message: string, technical?: string}>({ message: '' });
  
  // Status labels and colors
  const statusLabels: Record<OpportunityStatus, string> = {
    open: 'Em aberto',
    negotiation: 'Em negociação',
    won: 'Ganha',
    lost: 'Perdida',
    canceled: 'Cancelada'
  };
  
  const statusColors: Record<OpportunityStatus, string> = {
    open: 'bg-blue-100 text-blue-800',
    negotiation: 'bg-purple-100 text-purple-800',
    won: 'bg-green-100 text-green-800',
    lost: 'bg-red-100 text-red-800',
    canceled: 'bg-gray-100 text-gray-800'
  };
  
  // Carregar dados da oportunidade
  useEffect(() => {
    const fetchOpportunityData = async () => {
      try {
        setIsLoading(true);
        
        if (!id) {
          console.error('No opportunity ID provided');
          setErrorDetails({
            message: 'ID da oportunidade não fornecido',
            technical: 'O parâmetro ID está vazio ou não foi encontrado na URL'
          });
          return;
        }
        
        console.log(`Fetching opportunity with ID: ${id}`);
        try {
          // Tentar buscar oportunidade com a nova função que suporta múltiplos tipos de ID
          const opportunityData = await getOpportunityByAnyId(id);
          
          if (!opportunityData) {
            setErrorDetails({
              message: `Oportunidade com ID ${id} não encontrada`,
              technical: 'A busca retornou null ou undefined'
            });
            return;
          }
          
          console.log('Fetched opportunity data:', opportunityData);
          setOpportunity(opportunityData as OpportunityWithDetails);
          
          // Tentar buscar propostas relacionadas
          try {
            const proposalsData = await getBusinessProposals(id);
            console.log('Fetched proposals data:', proposalsData);
            setProposals(proposalsData as BusinessProposal[]);
          } catch (proposalsError) {
            console.warn('Error fetching proposals (non-fatal):', proposalsError);
            // Não falhamos completamente se as propostas não carregarem
            setProposals([]);
          }
        } catch (fetchError) {
          console.error('Error fetching opportunity:', fetchError);
          
          // Determinar tipo de erro para mensagem adequada
          let errorMessage = 'Não foi possível carregar os dados da oportunidade';
          let technicalDetails = fetchError.message;
          
          if (fetchError.code === '22P02') {
            errorMessage = 'O formato do ID da oportunidade não é válido';
            technicalDetails = 'UUID syntax error - Este ID não está no formato esperado pelo banco de dados';
          } else if (fetchError.message.includes('not found')) {
            errorMessage = `Oportunidade com ID ${id} não encontrada`;
            technicalDetails = 'O registro solicitado não existe no banco de dados';
          }
          
          setErrorDetails({
            message: errorMessage,
            technical: technicalDetails
          });
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchOpportunityData();
  }, [id]);
  
  // Handler para voltar à lista de oportunidades
  const handleBackClick = () => {
    navigate('/business-opportunities');
  };
  
  // Componente de erro
  const ErrorDisplay = () => (
    <div className="container w-full mx-auto py-10">
      <div className="text-center">
        <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
        <h2 className="text-2xl font-extrabold">Erro ao carregar oportunidade</h2>
        <p className="text-muted-foreground mt-2">
          {errorDetails.message || 'A oportunidade solicitada não existe ou não foi possível carregá-la.'}
        </p>
        {errorDetails.technical && (
          <div className="mt-4 p-4 bg-gray-100 rounded-md text-left max-w-2xl mx-auto">
            <p className="text-sm font-extrabold">Detalhes técnicos:</p>
            <p className="text-xs font-mono mt-1 text-gray-600">{errorDetails.technical}</p>
          </div>
        )}
        <Button onClick={handleBackClick} className="mt-6" variant="outline">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar para lista de oportunidades
        </Button>
      </div>
    </div>
  );
  
  // Mostrar mensagem se a oportunidade não for encontrada
  if (!opportunity) {
    return <ErrorDisplay />;
  }
  
  // Mostrar loading state
  if (isLoading) {
    return (
      <div className="container w-full mx-auto py-10">
        <div className="flex justify-center items-center flex-col h-64">
          <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
          <p className="text-lg text-muted-foreground">Carregando detalhes da oportunidade...</p>
          {id && <p className="text-sm text-muted-foreground mt-2">ID: {id}</p>}
        </div>
      </div>
    );
  }
  
  return (
    <div className="container w-full mx-auto py-6 space-y-6 px-4">
      {/* Cabeçalho com navegação */}
      <div className="flex justify-between items-center py-2 border-b">
        <div className="flex items-center space-x-3">
          <Button variant="ghost" size="icon" onClick={handleBackClick} className="h-8 w-8">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-extrabold">Oportunidade - {opportunity.name}</h1>
          <Badge className={`${statusColors[opportunity.status]} border text-sm px-3 py-1 ml-2`}>
            {statusLabels[opportunity.status]}
          </Badge>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => navigate(`/business-opportunities/${id}/edit`)}
          >
            <Edit className="h-4 w-4 mr-2" />
            Editar
          </Button>
        </div>
      </div>
      
      {/* Conteúdo principal */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Coluna da esquerda - Detalhes do cliente */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Building className="h-5 w-5 mr-2 text-muted-foreground" />
              Detalhes do Cliente
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center">
              <Avatar className="h-14 w-14 mr-4">
                {opportunity.customer.logo_url ? (
                  <AvatarImage src={opportunity.customer.logo_url} alt={opportunity.customer.name} />
                ) : (
                  <AvatarFallback className="text-lg">
                    {opportunity.customer.name.charAt(0)}
                  </AvatarFallback>
                )}
              </Avatar>
              <div>
                <h3 className="text-xl font-extrabold">{opportunity.customer.name}</h3>
                {opportunity.customer.business_identification_number && (
                  <p className="text-sm text-muted-foreground">
                    CNPJ: {opportunity.customer.business_identification_number}
                  </p>
                )}
              </div>
            </div>
            
            <Separator />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-extrabold text-muted-foreground mb-1">Contato Principal</h4>
                {opportunity.contact ? (
                  <div className="flex items-center">
                    <User className="h-4 w-4 mr-2 text-muted-foreground" />
                    <div>
                      <p className="text-base font-extrabold">{opportunity.contact.name}</p>
                      {opportunity.contact.email && (
                        <p className="text-sm text-muted-foreground">{opportunity.contact.email}</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Não definido</p>
                )}
              </div>
              
              <div>
                <h4 className="text-sm font-extrabold text-muted-foreground mb-1">Valor Total</h4>
                <p className="text-xl font-extrabold">
                  {formatCurrency(opportunity.total_value, opportunity.currency_code)}
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-extrabold text-muted-foreground mb-1">Probabilidade</h4>
                <div className="flex items-center">
                  <span className="text-lg font-extrabold mr-2">{opportunity.probability}%</span>
                  <div className="w-24 bg-gray-100 rounded-full h-1.5">
                    <div 
                      className={`h-1.5 rounded-full ${
                        opportunity.probability > 70 ? 'bg-green-500' : 
                        opportunity.probability > 40 ? 'bg-amber-500' : 'bg-red-500'
                      }`} 
                      style={{ width: `${opportunity.probability}%` }}
                    />
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-extrabold text-muted-foreground mb-1">Data Prevista de Fechamento</h4>
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-2 text-muted-foreground" />
                  <p className="text-base">
                    {opportunity.expected_close_date 
                      ? new Date(opportunity.expected_close_date).toLocaleDateString('pt-BR')
                      : 'Não definida'}
                  </p>
                </div>
              </div>
            </div>
            
            <Separator />
            
            <div>
              <h4 className="text-sm font-extrabold text-muted-foreground mb-1">Descrição</h4>
              <p className="text-base">{opportunity.description || 'Sem descrição'}</p>
            </div>
          </CardContent>
        </Card>
        
        {/* Coluna da direita - Timeline e ações */}
        <Card>
          <CardHeader>
            <CardTitle>Informações Adicionais</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="text-sm font-extrabold text-muted-foreground mb-1">Criado em</h4>
              <p className="text-base">{new Date(opportunity.created_at || '').toLocaleDateString('pt-BR')}</p>
            </div>
            
            <div>
              <h4 className="text-sm font-extrabold text-muted-foreground mb-1">Última Atualização</h4>
              <p className="text-base">{new Date(opportunity.updated_at || '').toLocaleDateString('pt-BR')}</p>
            </div>
            
            <Separator />
            
            <div className="space-y-2">
              <h4 className="text-sm font-extrabold text-muted-foreground">Ações Rápidas</h4>
              <div className="grid grid-cols-1 gap-2">
                <Button 
                  className="w-full justify-start" 
                  variant="outline"
                  onClick={() => setActiveTab('proposals')}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Ver Propostas ({proposals.length})
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Abas para Detalhes, Propostas, etc. */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="details">Detalhes</TabsTrigger>
          <TabsTrigger value="proposals">Propostas</TabsTrigger>
        </TabsList>
        
        <TabsContent value="details" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Detalhes da Oportunidade</CardTitle>
            </CardHeader>
            <CardContent>
              <p>
                Esta oportunidade está classificada como <Badge className={statusColors[opportunity.status]}>{statusLabels[opportunity.status]}</Badge> com 
                uma probabilidade de fechamento de <strong>{opportunity.probability}%</strong>.
              </p>
              {opportunity.description && (
                <div className="mt-4">
                  <h4 className="font-extrabold">Descrição:</h4>
                  <p className="mt-1">{opportunity.description}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="proposals" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Propostas</CardTitle>
              <Button size="sm" onClick={() => navigate(`/proposals/new/${id}`)}>
                Nova Proposta
              </Button>
            </CardHeader>
            <CardContent>
              {proposals.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhuma proposta registrada para esta oportunidade.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Título</TableHead>
                      <TableHead>Versão</TableHead>
                      <TableHead>Valor</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {proposals.map((proposal) => (
                      <TableRow key={proposal.id}>
                        <TableCell className="font-extrabold">{proposal.title}</TableCell>
                        <TableCell>v{proposal.version}</TableCell>
                        <TableCell>{formatCurrency(proposal.total_value, proposal.currency_code)}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{proposal.status}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => navigate(`/proposals/${proposal.id}`)}
                          >
                            Ver
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default OpportunityDetailSimple; 